import {
  FileText,
  Linkedin,
  Search,
  Mail,
  Users,
  MessageSquare,
  Award,
  Book,
} from "lucide-react";

const HRWhatYoullLearn = () => {
  const learningItems = [
    {
      icon: <FileText className="w-5 h-5 text-teal-400" />,
      title: "HR CV That Works",
      subtitle: "📝 Templates that actually get shortlisted",
      description: "Professional resume building for HR roles",
      competencyLevel: "Essential",
      department: "Talent Acquisition",
      skillTag: "Resume",
      borderColor: "border-teal-400/30",
      bgGradient: "from-teal-500 to-teal-700",
    },
    {
      icon: <Linkedin className="w-5 h-5 text-white-400" />,
      title: "LinkedIn That Shows You Belong in HR",
      subtitle: "🔗 No fluff. Just credibility.",
      description: "Build a professional HR presence online",
      competencyLevel: "Essential",
      department: "Personal Branding",
      skillTag: "LinkedIn",
      borderColor: "border-[#0A66C2]/30",
      bgGradient: "from-[#0A66C2] to-blue-700",
    },
    {
      icon: <Search className="w-5 h-5 text-purple-400" />,
      title: "Explore HR Roles the Smart Way",
      subtitle: "🔍 TA, HRBP, L&D, OD — get what suits YOU",
      description: "Understand different HR functions and find your fit",
      competencyLevel: "Strategic",
      department: "Career Planning",
      skillTag: "Career",
      borderColor: "border-purple-500/30",
      bgGradient: "from-purple-500 to-purple-700",
    },
    {
      icon: <Mail className="w-5 h-5 text-orange-400" />,
      title: "Job Hunt & Outreach",
      subtitle: "🧭 From Naukri to cold mails — the tools we actually use",
      description: "Master job portals and professional outreach",
      competencyLevel: "Operational",
      department: "Business Development",
      skillTag: "Outreach",
      borderColor: "border-orange-500/30",
      bgGradient: "from-orange-500 to-orange-700",
    },
    {
      icon: <Users className="w-5 h-5 text-pink-400" />,
      title: "Simulation: Recruiter for a Day",
      subtitle: "🧪 JD making, profile shortlisting, Naukri recruiter login",
      description: "Experience HR from the recruiter's perspective",
      competencyLevel: "Advanced",
      department: "Talent Acquisition",
      skillTag: "Recruiting",
      borderColor: "border-pink-500/30",
      bgGradient: "from-pink-500 to-pink-700",
    },
    {
      icon: <MessageSquare className="w-5 h-5 text-cyan-400" />,
      title: "Interview Confidence",
      subtitle:
        "🎙 Real prep for 'Why HR?', 'Your fit', 'How would you recruit?'",
      description: "Master HR interview questions and scenarios",
      competencyLevel: "Essential",
      department: "Professional Development",
      skillTag: "Interview",
      borderColor: "border-cyan-500/30",
      bgGradient: "from-cyan-500 to-cyan-700",
    },
  ];

  return (
    <div className="bg-black py-32 lg:py-16 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Department Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/30 backdrop-blur-sm">
              <Book className="w-4 h-4 text-blue-400" />
              <span className="text-sm font-semibold text-blue-400 tracking-wide uppercase">
                What You'll Learn
              </span>
            </div>
          </div>
          <div className="inline-block bg-slate-800/50 rounded-lg px-6 py-3 border border-slate-600/30 mb-6">
            <div className="text-xs text-gray-400 uppercase tracking-wider"></div>
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              Skills You Will Master
            </h2>
          </div>
          <p className="text-gray-400 text-lg">
            Built by practitioners. Backed by industry.
          </p>
        </div>

        <div className="space-y-8">
          {/* Desktop Grid View */}
          <div className="hidden md:grid md:grid-cols-2 md:gap-6">
            {learningItems.map((item, index) => (
              <div
                key={index}
                className={`group relative bg-black border ${item.borderColor} rounded-xl p-6 hover:border-opacity-70 transition-all duration-300 hover:bg-gradient-to-br hover:${item.bgGradient} hover:shadow-lg hover:shadow-pink-500/10 flex flex-col h-full`}
              >
                <div
                  className={`absolute -inset-0.5 bg-gradient-to-r ${item.bgGradient} rounded-xl blur opacity-0 group-hover:opacity-30 transition duration-300`}
                />

                <div className="relative flex-1 flex flex-col">
                  {/* Header with icon and skill tag */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div
                        className={`p-3 rounded-lg bg-gradient-to-br ${item.bgGradient} border ${item.borderColor} shrink-0`}
                      >
                        {item.icon}
                      </div>
                      <h3 className="text-lg font-bold text-white group-hover:text-pink-300 transition-colors">
                        {item.title}
                      </h3>
                    </div>
                    <div className="px-3 py-1 bg-gray-800/50 rounded-full text-xs font-sans text-gray-400 border border-gray-700">
                      {item.skillTag}
                    </div>
                  </div>

                  {/* Content - flex-1 to take available space */}
                  <div className="space-y-2 flex-1">
                    <p className="text-white md:text-sm font-medium">
                      {item.subtitle}
                    </p>
                    <p className="text-gray-400 text-sm group-hover:text-gray-200 transition-colors">
                      {item.description}
                    </p>
                  </div>

                  {/* Progress indicator - always at bottom */}
                  <div className="mt-4 flex items-center gap-2">
                    <div className="flex-1 h-1 bg-gray-800 rounded-full overflow-hidden">
                      <div
                        className={`h-full bg-gradient-to-r ${item.bgGradient} rounded-full w-0 group-hover:w-full transition-all duration-1000 delay-200`}
                      ></div>
                    </div>
                    <Award className="w-4 h-4 text-gray-600 group-hover:text-yellow-400 transition-colors" />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-4">
            {learningItems.map((item, index) => (
              <div
                key={index}
                className={`group relative bg-black border ${item.borderColor} rounded-xl p-6 hover:border-opacity-70 transition-all duration-300 hover:bg-gradient-to-br hover:${item.bgGradient}`}
              >
                <div
                  className={`absolute -inset-0.5 bg-gradient-to-r ${item.bgGradient} rounded-xl blur opacity-0 group-hover:opacity-30 transition duration-300`}
                />

                <div className="relative">
                  <div className="flex flex-col items-center text-center mb-4">
                    <div
                      className={`p-3 rounded-lg bg-gradient-to-br ${item.bgGradient} border ${item.borderColor} mb-3`}
                    >
                      {item.icon}
                    </div>
                    <h3 className="text-lg font-bold text-white group-hover:text-pink-300 transition-colors mb-2">
                      {item.title}
                    </h3>
                    <div className="px-3 py-1 bg-gray-800/50 rounded-full text-xs font-sans text-gray-400 border border-gray-700">
                      {item.skillTag}
                    </div>
                  </div>

                  <div className="space-y-2 text-center">
                    <p className="text-white font-medium">{item.subtitle}</p>
                    <p className="text-gray-400 text-sm group-hover:text-gray-200 transition-colors">
                      {item.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Bottom Summary - Updated */}
          <div className="mt-12 text-center">
            <div className="relative overflow-hidden rounded-xl bg-slate-900/30 border border-slate-600/30 p-8">
              {/* Dashboard Header */}
              <div className="mb-6">
                <div className="text-xs text-gray-400 uppercase tracking-wider">
                  Training Summary
                </div>
                <h3 className="text-xl font-bold text-white">
                  Learning Outcomes
                </h3>
              </div>

              {/* Key Insight */}
              <div className="bg-slate-800/50 rounded-lg p-6 border border-slate-600/30">
                {/* Mobile Layout - Icon at top, content below */}
                <div className="flex flex-col items-center justify-center gap-4 md:hidden">
                  <div className="bg-amber-500/20 rounded-full p-3">
                    <Award className="w-6 h-6 text-amber-400" />
                  </div>
                  <p className="text-xl font-medium text-white">
                    The essential skills no college teaches, but every HR
                    professional needs.
                  </p>
                </div>

                {/* Desktop Layout - Icon and content side by side */}
                <div className="hidden md:flex md:items-center md:justify-center md:gap-4">
                  <div className="bg-amber-500/20 rounded-full p-3">
                    <Award className="w-6 h-6 text-amber-400" />
                  </div>
                  <p className="text-sm md:text-lg font-medium text-white">
                    The essential skills no college teaches, but every HR
                    professional needs.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HRWhatYoullLearn;
